import dayjs from 'dayjs';
import * as React from 'react';
import { View, TouchableOpacity } from 'react-native';
import { Edge } from 'react-native-safe-area-context';
import { s } from 'react-native-wind';
import Feather from '@expo/vector-icons/Feather';
import { FeedbackValue } from '../../api/feedback/feedback.models';
import { Team, TeamStatus } from '../../api/team/team.models';
import {
  HomeScreenHeader,
  Layout,
  Paragraph,
  ParagraphSmall,
  Separator,
  Subtitle,
} from '../../components';
import { Config } from '../../config';
import {
  useCastFeedback,
  useGetUserFeedbacks,
} from '../../hooks/api/feedback.api';
import { useGetMyTeams } from '../../hooks/api/team.api';
import { useGetMyDetails } from '../../hooks/api/user.api';
import { useLanguage } from '../../locale';
import {
  COMPLETED_SURVEYS_SCREEN,
  FEEDBACK_DETAILS_SCREEN,
  SURVEY_DETAIL_SCREEN,
} from '../../navigation/authenticated/authenticated.types';
import { FEEDBACKS_SCREEN } from '../../navigation/authenticatedWithBottomTab/authenticated.types';
import { GenericScreenProps } from '../../navigation/core/types';
import { NoData } from '../stats/components/NoData';
import { TeamCard } from './components/TeamCard';
import * as Haptics from '../../portability/services/Haptics/Haptics';
import { AlreadyVotedTeamCard } from './components/AlreadyVotedTeamCard';
import {
  useGetCurrentSurvey,
  useGetPendingSurveys,
  useGetCompletedSurveys,
} from '../../hooks/api/survey.api';
import useUserData from '../../hooks/useUserData';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import { SurveyCard } from './components/SurveyCard';

const feedbacksSafeAreaEdges: Edge[] = ['left', 'right', 'top'];

type FeedbacksScreensProps = {} & GenericScreenProps<typeof FEEDBACKS_SCREEN>;

const FeedbacksScreens: React.FC<FeedbacksScreensProps> = ({ navigation }) => {
  const [t] = useLanguage();
  const [FeatureFlags] = useFeatureFlags();

  const { mutateAsync: castFeedback } = useCastFeedback();
  const { data: myDetails } = useGetMyDetails();
  const { data: teams = [] } = useGetMyTeams(TeamStatus.Active);
  const { data: feedbacks } = useGetUserFeedbacks({
    from: dayjs().format('YYYY-MM-DD'),
    to: dayjs().format('YYYY-MM-DD'),
  });
  const { company } = useUserData();
  const { data: currentSurvey, isLoading: isCurrentSurveyLoading } =
    useGetCurrentSurvey(FeatureFlags.PulseSurvey ? company?.id : undefined);
  const { data: pendingSurveys, isLoading: isPendingSurveysLoading } =
    useGetPendingSurveys(FeatureFlags.PulseSurvey ? company?.id : undefined);
  const { data: completedSurveys } = useGetCompletedSurveys(
    FeatureFlags.PulseSurvey ? company?.id : undefined
  );

  const teamToShowNoVote = React.useMemo(() => {
    const noVoteTeams = teams.filter((t) => {
      const teamFeedback = feedbacks?.find((f) => f.teamId === t.id);
      return !teamFeedback;
    });
    return noVoteTeams.sort();
  }, [feedbacks, teams]);

  const teamWithVotes = React.useMemo(() => {
    const votedTeams = teams.filter((t) => {
      const teamFeedback = feedbacks?.find((f) => f.teamId === t.id);
      return teamFeedback;
    });

    return votedTeams;
  }, [feedbacks, teams]);

  const noDataDescription = React.useMemo(
    () => [t('feedback.notEnoughtData')],
    [t]
  );

  const onCastFeedback = React.useCallback(
    async (feedbackValue: FeedbackValue, teamId: string) => {
      if (!feedbackValue || !myDetails) return;

      try {
        await castFeedback({
          teamId,
          value: feedbackValue,
          voteAnonymous: myDetails.voteAnonymous,
        });
        navigation.navigate(FEEDBACK_DETAILS_SCREEN, {
          teamId,
          userId: myDetails.id,
        });
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      } catch (e) {}
    },
    [castFeedback, myDetails, navigation]
  );

  const navigateToRightTeam = React.useCallback(
    (t: Team) => {
      return () => {
        if (!myDetails) return;
        navigation.navigate(FEEDBACK_DETAILS_SCREEN, {
          teamId: t.id,
          userId: myDetails.id,
        });
      };
    },
    [myDetails, navigation]
  );

  // Filter pending surveys to exclude current survey
  const filteredPendingSurveys = React.useMemo(() => {
    if (!pendingSurveys?.item?.surveyList) {
      return [];
    }
    return pendingSurveys.item.surveyList.filter(
      (survey) =>
        !currentSurvey?.item || survey.surveyId !== currentSurvey.item.surveyId
    );
  }, [pendingSurveys, currentSurvey]);

  // Calculate days remaining until endDate
  const daysRemaining = React.useMemo(() => {
    if (!pendingSurveys?.item?.endDate) {
      return null;
    }
    const endDate = dayjs(pendingSurveys.item.endDate);
    const today = dayjs();
    const diff = endDate.diff(today, 'day');
    return diff > 0 ? diff : null;
  }, [pendingSurveys?.item?.endDate]);

  // Only show the survey section if there's a current survey or pending surveys
  const hasSurveys = currentSurvey?.item || filteredPendingSurveys.length > 0;

  // Check if there are completed surveys to show the link
  const hasCompletedSurveys =
    completedSurveys?.items && completedSurveys.items.length > 0;

  return (
    <Layout
      safeAreaEdges={feedbacksSafeAreaEdges}
      showNavbar={false}
      customNavbar={<HomeScreenHeader title={t('feedback.title')} />}
    >
      {teamToShowNoVote.length > 0 ? (
        <>
          <Subtitle style={s`mb-2`}>
            {t('feedback.continuousFeedbackTitle')}
          </Subtitle>
          <View>
            {teamToShowNoVote?.map((t, i) => {
              const isFirst = i === 0;
              const teamFeedback = feedbacks?.find((f) => f.teamId === t.id);
              return (
                <TeamCard
                  onFacePress={onCastFeedback}
                  containerStyle={s`${!isFirst ? 'mt-4' : ''}`}
                  selectedVote={teamFeedback?.value}
                  key={t.id}
                  team={t}
                />
              );
            })}
          </View>
        </>
      ) : null}
      {teamWithVotes.length > 0 ? (
        <View style={s`${teamToShowNoVote.length > 0 ? 'mt-6' : ''}`}>
          <Subtitle>{t('feedback.alreadyVoted')}</Subtitle>
          <View style={s`mt-4`}>
            {teamWithVotes.map((t, i) => {
              const isFirst = i === 0;
              const teamFeedback = feedbacks?.find((f) => f.teamId === t.id);
              const navigateToAlreadyVotedTeam = navigateToRightTeam(t);

              return teamFeedback ? (
                <AlreadyVotedTeamCard
                  onCardPress={navigateToAlreadyVotedTeam}
                  key={t.id}
                  containerStyle={s`${!isFirst ? 'mt-4' : ''}`}
                  team={t}
                  feedback={teamFeedback}
                />
              ) : (
                <React.Fragment key={t.id}></React.Fragment>
              );
            })}
          </View>
        </View>
      ) : null}
      {teams.length === 0 ? <NoData descriptions={noDataDescription} /> : null}

      {/* Survey Section - Only render when feature flag is enabled, survey data has finished loading and there are surveys */}
      {FeatureFlags.PulseSurvey &&
        !isCurrentSurveyLoading &&
        !isPendingSurveysLoading &&
        hasSurveys &&
        teams.length > 0 && (
          <>
            <Separator style={s`mt-6`} />
            <View style={s`mt-6`}>
              <View style={s`flex-row justify-between items-center`}>
                <Subtitle>{t('survey.title')}</Subtitle>
                {hasCompletedSurveys && (
                  <TouchableOpacity
                    style={s`flex-row items-center`}
                    onPress={() =>
                      navigation.navigate(COMPLETED_SURVEYS_SCREEN as never)
                    }
                  >
                    <Paragraph style={s`text-primary mr-1`}>
                      {t('survey.completedSurveys')}
                    </Paragraph>
                    <Feather
                      name="rotate-ccw"
                      size={15}
                      color={Config.colorPrimary}
                      style={s`pl-1`}
                    />
                  </TouchableOpacity>
                )}
              </View>

              {/* Current Survey */}
              <View style={s`mt-4`}>
                {currentSurvey?.item ? (
                  <SurveyCard
                    survey={currentSurvey.item}
                    onCardPress={() => {
                      if (currentSurvey.item?.surveyId) {
                        navigation.navigate(SURVEY_DETAIL_SCREEN, {
                          surveyId: currentSurvey.item.surveyId,
                        });
                      }
                    }}
                  />
                ) : (
                  <View style={s`py-4`}>
                    <ParagraphSmall style={s`text-text-secondary`}>
                      {t('survey.noSurveys')}
                    </ParagraphSmall>
                  </View>
                )}
              </View>

              {/* Pending Surveys - Only show if there are filtered surveys */}
              {filteredPendingSurveys.length > 0 && (
                <View style={s`mt-4`}>
                  <View
                    style={s`flex-row justify-between items-center mt-4 mb-2`}
                  >
                    <Subtitle>{t('survey.pendingSurveys')}</Subtitle>
                    {daysRemaining && (
                      <ParagraphSmall style={s`text-text-secondary`}>
                        {t('survey.expiresIn', { days: daysRemaining })}
                      </ParagraphSmall>
                    )}
                  </View>
                  {filteredPendingSurveys.map((survey, index) => (
                    <View
                      key={survey.surveyId}
                      style={index > 0 ? s`mt-4` : undefined}
                    >
                      <SurveyCard
                        survey={survey}
                        onCardPress={() => {
                          navigation.navigate(SURVEY_DETAIL_SCREEN, {
                            surveyId: survey.surveyId,
                          });
                        }}
                      />
                    </View>
                  ))}
                </View>
              )}
            </View>
          </>
        )}
    </Layout>
  );
};

export default FeedbacksScreens;
